import { useEffect, useState } from 'react';
import { useLoaderData, useParams } from 'react-router-dom';
import Breadcrumbs from '../components/breadcrumbs';
import type { OptionType } from '../types/forms.type';
import { MapClient } from '../types/global';
import GetLatestSnapshot from '../services/adapters/snapshotResponse';
import { Snapshot } from '../types/core.type';
import DemographicBreakdown from '../components/debt/demographicBreakdown';
import DebtGrid from '../components/debt/debtGrid';
import { All } from '../types/enums';
import PostDebtInsights from '../services/adapters/debtInsights';
import useRequestState from '../hooks/useRequestState';
import DebtListCard from '../components/debt/debtListCard';
import customIcons from '../utils/icons';
import { DebtMetricItemInterface } from '../types/debt.type';
import DebtMap from '../components/debt/map';
import PostMapClient from '../services/adapters/mapClient';
import SimpleDropdown from '../components/form-inputs/simpleDropdown';
import GetDebtMetricDropdownOptions from '../services/adapters/debtMetricOptions';
import { extendFiltersWithWard } from '../utils/filters';
import useSnapshotWardFilters from '../hooks/useSnapshotWardFilters';
import SnapshotWardDropdown from '../components/snapshotWardDropdown';

export function loader() {
  const snapshotAdapter = new GetLatestSnapshot();
  const snapshot = snapshotAdapter.adaptResponse();
  const mapClientAdapter = new PostMapClient();
  const mapClient = mapClientAdapter.adaptResponse();
  return Promise.all([snapshot, mapClient]);
}

export default function DebtInsights() {
  const { debtType } = useParams() as { debtType: string };
  const [snapshot, mapClient] = useLoaderData() satisfies [Snapshot, MapClient];
  const [selectedMetric, setSelectedMetric] = useState<OptionType>({ label: '', value: '' });

  const {
    selectedSnapshot,
    selectedLocalAuthority,
    selectedWard,
    handleSnapshotChange,
    handleLocalAuthorityChange,
    handleWardChange,
  } = useSnapshotWardFilters(snapshot);
  const {
    data: debtInsightsData,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<DebtMetricItemInterface>();

  const {
    data: debtMetricDropdownOptions,
    handleRequest: handleDebtMetricDropdownOptionsRequest,
  } = useRequestState<OptionType[]>();

  useEffect(() => {
    if (!selectedSnapshot.value || !snapshot) return;
    const adapter = new PostDebtInsights();
    const response = adapter.adaptResponse({
      snapshot: selectedSnapshot.value, liftDataId: selectedLocalAuthority.value, wardName: selectedWard.value, debtType,
    });
    void handleRequest(response);
    const dropdownAdapter = new GetDebtMetricDropdownOptions();
    const dropdownResponse = dropdownAdapter.adaptResponse(debtType);
    void handleDebtMetricDropdownOptionsRequest(dropdownResponse);
    void dropdownResponse.then((options) => {
      const allOption = options.find((option) => String(option.label) === String(All.label));
      if (allOption) {
        setSelectedMetric(allOption);
      }
    });
  }, [selectedSnapshot, selectedLocalAuthority, selectedWard, debtType]);

  const debtGridDataWithWardFilter = debtInsightsData?.grid.map((item) => ({
    ...item,
    filters: extendFiltersWithWard(item.filters, selectedWard.value),
  })) || [];

  return (
    <div className="flex flex-col w-full gap-4 py-5 px-6">
      <Breadcrumbs />
      <header id="debt-page-header" className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <div className="text-3xl font-bold text-piptext-default">{debtInsightsData?.title}</div>
        </div>
        <span id="debt-page-description" className="text-pipgrey text-sm font-normal">
          {debtInsightsData?.description}
        </span>

        <SnapshotWardDropdown
          selectedSnapshot={selectedSnapshot || { label: snapshot.formattedSnapshot, value: snapshot.rawSnapshot }}
          selectedWard={selectedWard}
          selectedLocalAuthority={selectedLocalAuthority}
          onSnapshotChange={handleSnapshotChange}
          onWardChange={handleWardChange}
          onLocalAuthorityChange={handleLocalAuthorityChange}
        />
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <FallbackWrapper loadingMessage="...loading">
          <DebtGrid data={debtGridDataWithWardFilter || []} />
        </FallbackWrapper>
        <div id="debt-ward-map" className="bg-white border border-pipgrey-border rounded-xl">
          <div className="relative flex flex-col h-full w-full transition-all duration-500 ease-in-out order-first">
            <DebtMap
              mapConfig={mapClient}
              dropdownOptions={[]}
              dimensions={{
                snapshot: selectedSnapshot.value || snapshot.rawSnapshot,
                liftDataId: selectedLocalAuthority.value,
                wardName: selectedWard.value,
                metricName: selectedMetric.value,
                debtType,
              }}
            />
            <div className="absolute ml-4 mt-4 h-fit rounded-lg min-w-1/3 max-w-1/2">
              {debtMetricDropdownOptions && (
              <SimpleDropdown
                id="metric-names"
                onChange={(value) => {
                  const option = debtMetricDropdownOptions?.find((o) => o.value === value);
                  if (option) setSelectedMetric(option);
                }}
                options={debtMetricDropdownOptions}
                selected={selectedMetric.value}
              />
              )}
            </div>
          </div>
        </div>
        <div id="debt-list-cards" className="bg-white border border-pipgrey-border rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {debtInsightsData?.columns && debtInsightsData.columns.map((column) => {
              const metricsWithWardFilter = column.metrics.map((metric) => ({
                ...metric,
                filters: extendFiltersWithWard(metric.filters, selectedWard.value),
              })); return (
                <DebtListCard
                  key={column.id}
                  title={column.displayName}
                  icon={column.icon as keyof typeof customIcons}
                  items={metricsWithWardFilter || []}
                />
              );
            })}
          </div>
        </div>
        <DemographicBreakdown
          snapshot={selectedSnapshot.value || snapshot.rawSnapshot}
          localAuthority={selectedLocalAuthority.value}
          wardWithOther={selectedWard.value}
          debtType={debtType}
        />
      </div>
    </div>
  );
}
